import { useCallback, useEffect, useRef, useState } from 'react';

import { sb } from './lib/supabase.js';
import * as api from './lib/api.js';
import { PERUT_SOLA_ID } from './config.js';
import { useUI } from './components/ui/feedback.jsx';
import { SolaProvider } from './sola.jsx';
import { initLang } from './i18n/index.js';

import { Login } from './screens/Login.jsx';
import { Onboarding } from './screens/Onboarding.jsx';
import { Student } from './screens/Student.jsx';
import { Admin } from './screens/Admin.jsx';
import { Splash } from './screens/Splash.jsx';
import { Zasebnost } from './screens/Zasebnost.jsx';

const JAVNA_ZASEBNOST = typeof window !== 'undefined' && (window.location.pathname === '/zasebnost' || new URLSearchParams(window.location.search).has('zasebnost'));

/*
  Vrhnja komponenta: skrbi za prijavo, ugotovi vlogo, naloži šolo in
  podatke ter izbere zaslon.

  Vloge:
    platforma   Luka — vidi vse šole, dela kot inštruktor izbrane šole
    instruktor  član šole (sole_clani) — vidi svojo šolo
    tecajnik    vsi ostali — svoj profil v svoji šoli

  Zaslon = 'loading' | 'login' | 'onboarding' | 'student' | 'admin' | 'error'.
*/

const SOLA_KEY = 'perut-sola';

const readChosenSola = () => {
  try {
    return localStorage.getItem(SOLA_KEY);
  } catch {
    return null;
  }
};

export function App() {
  const { toast } = useUI();

  const [screen, setScreen] = useState('loading');
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [vloga, setVloga] = useState(null); // { platforma, clanstva }
  const [sola, setSola] = useState(null);
  const [programRows, setProgramRows] = useState([]);
  const [data, setData] = useState(null); // podatki tečajnika ali inštruktorja
  const [explanations, setExplanations] = useState({});
  const [fatal, setFatal] = useState(null);

  // Osvežitev žetona ne sme sprožiti ponovnega nalaganja vsega.
  const loadedUid = useRef(null);
  const solaRef = useRef(null);

  const isInstructor = !!vloga && (vloga.platforma || vloga.clanstva.length > 0);

  /** Naloži šolo, program in podatke za inštruktorja ali tečajnika. */
  const loadForSola = useCallback(async (solaId, asInstructor, uid, clanstva) => {
    const [s, rows, exp] = await Promise.all([
      api.loadSola(solaId),
      api.loadProgram(solaId),
      api.loadExplanations(solaId),
    ]);
    let d;
    if (asInstructor) {
      // Inštruktorji te šole niso tečajniki — ne kažemo jih na seznamu.
      const clani = await api.loadClani(solaId).catch(() => clanstva ?? []);
      d = await api.loadAdmin(solaId, clani.map((c) => c.email));
    } else {
      d = await api.loadStudent(uid);
    }
    solaRef.current = solaId;
    setSola(s);
    setProgramRows(rows);
    setExplanations(exp);
    setData(d);
    initLang(s?.jezik);
  }, []);

  const load = useCallback(
    async (s) => {
      const v = await api.loadVloga(s);
      setVloga(v);
      const p = await api.getProfile(s.user.id);
      setProfile(p);

      const instructor = v.platforma || v.clanstva.length > 0;
      if (instructor) {
        // Platforma začne v Perutu ali v nazadnje odprti šoli; inštruktor v svoji.
        const remembered = readChosenSola();
        const allowed = v.platforma
          ? remembered || PERUT_SOLA_ID
          : v.clanstva.find((c) => c.sola_id === remembered)?.sola_id ?? v.clanstva[0].sola_id;
        await loadForSola(allowed, true, s.user.id, v.clanstva);
        setScreen('admin');
        return;
      }

      if (!p?.ime) {
        setScreen('onboarding');
        return;
      }

      await loadForSola(p.sola_id ?? PERUT_SOLA_ID, false, s.user.id);
      setScreen('student');
    },
    [loadForSola],
  );

  useEffect(() => {
    const {
      data: { subscription },
    } = sb.auth.onAuthStateChange((_event, s) => {
      setSession(s);

      if (!s) {
        loadedUid.current = null;
        setProfile(null);
        setData(null);
        setVloga(null);
        setScreen('login');
        return;
      }

      if (loadedUid.current === s.user.id) return;
      loadedUid.current = s.user.id;

      // setTimeout(0): Supabase odsvetuje await znotraj tega povratnega klica.
      setTimeout(() => {
        load(s).catch((e) => {
          setFatal(e.message ?? 'Podatkov ni bilo mogoče naložiti.');
          setScreen('error');
        });
      }, 0);
    });

    return () => subscription.unsubscribe();
  }, [load]);

  /** Ponovno naloži vse, kar trenutni uporabnik vidi. */
  const refresh = useCallback(async () => {
    if (!session || !solaRef.current) return;
    try {
      await loadForSola(solaRef.current, isInstructor, session.user.id, vloga?.clanstva);
      if (!isInstructor) setProfile(await api.getProfile(session.user.id));
    } catch (e) {
      toast.bad(e.message ?? 'Osvežitev ni uspela.');
    }
  }, [session, isInstructor, vloga, toast, loadForSola]);

  /** Inštruktor ali platforma preklopi na drugo šolo. */
  const switchSola = useCallback(
    async (solaId) => {
      if (!session) return;
      try {
        localStorage.setItem(SOLA_KEY, solaId);
      } catch {
        /* ni ključno */
      }
      setScreen('loading');
      try {
        await loadForSola(solaId, true, session.user.id, vloga?.clanstva);
        setScreen('admin');
      } catch (e) {
        setFatal(e.message ?? 'Šole ni bilo mogoče naložiti.');
        setScreen('error');
      }
    },
    [session, vloga, loadForSola],
  );

  const signOut = useCallback(async () => {
    await api.signOut();
    loadedUid.current = null;
    setScreen('login');
    setProfile(null);
    setData(null);
    setVloga(null);
  }, []);

  if (screen === 'loading') return <Splash />;

  if (screen === 'error') {
    return (
      <Splash
        error={fatal}
        onRetry={() => {
          setScreen('loading');
          setFatal(null);
          if (session) load(session).catch((e) => { setFatal(e.message); setScreen('error'); });
        }}
        onSignOut={signOut}
      />
    );
  }

  // Javna politika zasebnosti brez prijave: trgovini (Google Play, App Store)
  // zahtevata naslov, ki se odpre brez računa. /zasebnost → index.html (SPA).
  if (JAVNA_ZASEBNOST) return <Zasebnost onClose={() => window.location.replace('/')} />;

  if (screen === 'login') return <Login />;

  if (screen === 'onboarding') {
    return (
      <Onboarding
        session={session}
        onDone={async () => {
          setScreen('loading');
          await load(session);
        }}
        onSignOut={signOut}
      />
    );
  }

  if (screen === 'admin') {
    const mojaVloga = vloga.platforma
      ? 'platforma'
      : vloga.clanstva.find((c) => c.sola_id === sola?.id)?.vloga ?? 'instruktor';
    return (
      <SolaProvider sola={sola} programRows={programRows} vloga={mojaVloga} platforma={vloga.platforma}>
        <Admin
          session={session}
          data={data}
          explanations={explanations}
          clanstva={vloga.clanstva}
          onRefresh={refresh}
          onSignOut={signOut}
          onSwitchSola={switchSola}
          onSolaChanged={async () => setSola(await api.loadSola(sola.id))}
          onProgramChanged={async () => setProgramRows(await api.loadProgram(sola.id))}
          setExplanations={setExplanations}
        />
      </SolaProvider>
    );
  }

  return (
    <SolaProvider sola={sola} programRows={programRows} vloga="tecajnik">
      <Student
        session={session}
        profile={profile}
        data={data}
        explanations={explanations}
        onRefresh={refresh}
        onSignOut={signOut}
      />
    </SolaProvider>
  );
}
